// aiCall.ts — AI helper routing through Supabase Edge Functions
// ANTHROPIC_API_KEY is server-side only. Users never need an API key.
// Single chokepoint for all ai-narrative calls: handles timeout, retry,
// JSON parse, and structured error kinds. See LESSONS.md L3.

import { supabase, isSupabaseEnabled } from "./supabase";

const EDGE_NARRATIVE = "ai-narrative";
const EDGE_CHAT = "ai-chat";

// ─── Error types ─────────────────────────────────────────────────────────────

export type AiCallErrorKind = "timeout" | "server" | "network" | "rate-limit" | "parse" | "unknown";

export interface AiCallError {
  kind: AiCallErrorKind;
  status?: number;
  message: string;
  rawResponse?: string;
}

export type AiCallResult<T> = { ok: true; data: T } | { ok: false; error: AiCallError };

// ─── Helpers ─────────────────────────────────────────────────────────────────

function stripJsonFences(text: string): string {
  return text.replace(/^```json\s*/i, "").replace(/```\s*$/, "").trim();
}

function classifyError(status: number | undefined, message: string): AiCallErrorKind {
  if (status === 429 || status === 529) return "rate-limit";
  if (status && status >= 500) return "server";
  if (message.includes("timeout") || message.includes("abort") || message.includes("Timeout")) return "timeout";
  if (message.includes("network") || message.includes("fetch") || message.includes("Failed to fetch")) return "network";
  return "unknown";
}

function isTransient(kind: AiCallErrorKind): boolean {
  return kind === "timeout" || kind === "server" || kind === "network" || kind === "rate-limit";
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

// ─── User-friendly error messages ────────────────────────────────────────────

export function aiErrorMessage(error: AiCallError): string {
  switch (error.kind) {
    case "timeout": return "AI service is slow — please try again.";
    case "rate-limit": return "AI service temporarily busy — please wait a moment and try again.";
    case "server": return "AI service temporarily unavailable — please try again.";
    case "network": return "Network error — check your connection and try again.";
    case "parse": return "AI returned an invalid response — please try again.";
    default: return "Could not generate summary — please try again.";
  }
}

// ─── Non-streaming narrative call (Result type, never throws) ────────────────

export async function callAi<T = string>(opts: {
  systemPrompt: string;
  userPrompt: string;
  maxTokens?: number;
  timeoutMs?: number;
  retries?: number;
  parseJson?: boolean;
}): Promise<AiCallResult<T>> {
  const {
    systemPrompt,
    userPrompt,
    maxTokens = 600,
    timeoutMs = 30000,
    retries = 1,
    parseJson = false,
  } = opts;

  if (!isSupabaseEnabled() || !supabase) {
    return { ok: false, error: { kind: "network", message: "AI features require a server connection" } };
  }

  const messages = [{ role: "user", content: userPrompt }];
  let lastError: AiCallError | null = null;
  const maxAttempts = 1 + retries;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0 && lastError) {
      const delay = lastError.kind === "rate-limit" ? 1500 : 500;
      console.log(`[aiCall] retry ${attempt}/${retries} after ${delay}ms (${lastError.kind})`);
      await sleep(delay);
    }

    try {
      console.log(`[aiCall] invoking ${EDGE_NARRATIVE} (attempt ${attempt + 1}/${maxAttempts})`);

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);

      const res = await supabase.functions.invoke(EDGE_NARRATIVE, {
        body: { systemPrompt, messages, maxTokens },
      });

      clearTimeout(timer);

      if (res.error) {
        let errMsg = res.error.message ?? "Edge Function error";
        let status: number | undefined;
        try {
          const ctx = (res.error as unknown as { context?: Response }).context;
          if (ctx) {
            status = ctx.status;
            const body = await ctx.text();
            if (body) errMsg = body;
          }
        } catch { /* ignore body-read failure */ }

        const kind = classifyError(status, errMsg);
        lastError = { kind, status, message: errMsg };
        console.error(`[aiCall] error (attempt ${attempt + 1}):`, { kind, status, message: errMsg });

        if (isTransient(kind) && attempt < maxAttempts - 1) continue;
        return { ok: false, error: lastError };
      }

      const rawText = (res.data as { text?: string })?.text ?? "";
      console.log("[aiCall] response received, length:", rawText.length);

      if (parseJson) {
        try {
          const cleaned = stripJsonFences(rawText);
          const parsed = JSON.parse(cleaned) as T;
          return { ok: true, data: parsed };
        } catch (parseErr) {
          const error: AiCallError = {
            kind: "parse",
            message: `JSON parse failed: ${(parseErr as Error).message}`,
            rawResponse: rawText,
          };
          console.error("[aiCall] JSON parse error:", { error: (parseErr as Error).message, rawResponse: rawText.slice(0, 500) });
          // No retry on parse errors — deterministic failure
          return { ok: false, error };
        }
      }

      return { ok: true, data: rawText as T };

    } catch (err) {
      const message = (err as Error)?.message ?? "Unknown error";
      const isAbort = message.includes("abort") || (err as Error)?.name === "AbortError";
      const kind: AiCallErrorKind = isAbort ? "timeout" : "network";
      lastError = { kind, message };
      console.error(`[aiCall] exception (attempt ${attempt + 1}):`, { kind, message });

      if (isTransient(kind) && attempt < maxAttempts - 1) continue;
      return { ok: false, error: lastError };
    }
  }

  return { ok: false, error: lastError ?? { kind: "unknown", message: "All retries exhausted" } };
}

// ─── Legacy wrapper (for callers not yet migrated) ──────────────────────────
// Preserved temporarily — all callers should migrate to callAi.

export async function callAiNarrative(opts: {
  systemPrompt: string;
  messages: { role: string; content: string }[];
  maxTokens?: number;
}): Promise<string> {
  const userPrompt = opts.messages.map((m) => m.content).join("\n\n");
  const result = await callAi<string>({
    systemPrompt: opts.systemPrompt,
    userPrompt,
    maxTokens: opts.maxTokens,
  });
  if (!result.ok) throw new Error(result.error.message);
  return result.data;
}

// ─── Streaming chat call ──────────────────────────────────────────────────────
// Returns a ReadableStream<Uint8Array> of raw SSE bytes from Anthropic.
// Throws Error("upgrade_required") if the user is on the free tier.
export async function callAiChatStreaming(opts: {
  systemPrompt: string;
  messages: { role: string; content: string }[];
}): Promise<ReadableStream<Uint8Array>> {
  const { systemPrompt, messages } = opts;

  if (!isSupabaseEnabled() || !supabase) {
    throw new Error("AI features require a server connection");
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

  // Include the user's JWT so the Edge Function can check subscription tier
  const { data: { session } } = await supabase.auth.getSession();
  const authHeader = session?.access_token ? `Bearer ${session.access_token}` : "";

  const res = await fetch(`${supabaseUrl}/functions/v1/${EDGE_CHAT}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "apikey": supabaseKey,
      ...(authHeader ? { "Authorization": authHeader } : {}),
    },
    body: JSON.stringify({ systemPrompt, messages }),
  });

  if (res.status === 403) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    if (body?.error === "upgrade_required") {
      throw new Error("upgrade_required");
    }
    throw new Error("Access denied");
  }

  if (!res.ok) {
    const err = await res.text();
    throw new Error(err || `Edge Function error ${res.status}`);
  }

  if (!res.body) throw new Error("No response body from Edge Function");
  return res.body;
}
